import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import MovieFilterIcon from "@mui/icons-material/MovieFilter";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { toast } from "react-toastify";
import "@wooglobe/vre-editor/style.css";
import api from "../../../conf/api";
import EditorTemplateGallery from "./EditorTemplateGallery";
import VideoEditorWorkspace from "./VideoEditorWorkspace";
import MRSSThumbnailCreator from "./MRSSThumbnailCreator";
import useVideoMetadata from "./useVideoMetadata";
import { createEditorServices } from "./editorServices";
import TemplateSlotSwitch from "./TemplateSlotSwitch";
import { pairFor } from "../BrandPublish/hooks/templateSlots";
import {
  asPreselected,
  buildRenderPayload,
  editSourceUrl,
  hasLocalMedia,
  loadStartTemplate,
  pickStartTemplate,
  probeVideoMetadata,
  resolveDeliveryUrl,
  resumeDocumentFor,
  savedTemplateIdOf,
  useEditorUserKey,
} from "./vreEditSession";

const DIALOG_STEPS = ["Select Template", "Edit Video"];
const NO_CONFIRM = { open: false, title: "", message: "", confirmLabel: "", onConfirm: null };
const NO_DOC = { key: null, doc: null };

const cancelSx = { borderRadius: 1.5, textTransform: "none", fontWeight: 600, borderColor: "#e2e8f0", color: "text.secondary" };
const indigoOutlineSx = {
  borderRadius: 1.5,
  textTransform: "none",
  fontWeight: 600,
  borderColor: alpha("#4338ca", 0.4),
  color: "#4338ca",
  "&:hover": { borderColor: "#4338ca", bgcolor: alpha("#4338ca", 0.06) },
};
const stepperSx = {
  "& .MuiStepLabel-label": {
    color: "rgba(255,255,255,0.5)",
    fontSize: 12,
    fontWeight: 500,
    "&.Mui-active": { color: "white", fontWeight: 700 },
    "&.Mui-completed": { color: "rgba(255,255,255,0.7)" },
  },
  "& .MuiStepIcon-root": {
    fontSize: 18,
    color: "rgba(255,255,255,0.25)",
    "&.Mui-active": { color: "#a5b4fc" },
    "&.Mui-completed": { color: "#86efac" },
  },
  "& .MuiStepConnector-line": { borderColor: "rgba(255,255,255,0.2)" },
};

/**
 * The video editor dialog: pick a template (unless the brand has one), edit the
 * video in the embedded VRE editor, and save the document as the edit payload
 * `[{ engine: "vre", template_id, template_version, document }]`.
 *
 * `onThumbnailSave`, if provided, adds a "Capture Thumbnail" action that
 * snapshots the editor's current frame (via the VRE preview-frame endpoint)
 * and opens it in the same Thumbnail Studio used elsewhere, with its
 * black/blurred-sides landscape option for vertical frames.
 */
function VreVideoEditDialog({
  open,
  onClose,
  video,
  onSave,
  // Thumbnail capture: a snapshot of the editor's current frame opens the
  // Thumbnail Studio (black/blurred sides for a vertical video), same as the
  // Creatomate editor. Omit to hide the "Capture Thumbnail" control.
  onThumbnailSave = undefined,
  preselectedTemplate = null,
  // The brand's horizontal template for this platform. A landscape video opens
  // on it; anything else opens on `preselectedTemplate`, the vertical one.
  preselectedHorizontalTemplate = null,
  // TikTok cover frame: current value (seconds) + setter. When provided, a
  // "Set as Cover" control appears under the editor.
  coverTimestamp = undefined,
  onCoverTimestampSave = undefined,
  // Raw videos picked in RawVideoPicker to merge back-to-back instead of the
  // single watermarked/edited video. Empty/absent: behaviour is unchanged.
  rawVideos = null,
  // The payload this dialog's onSave produced earlier. Re-editing reopens that
  // document instead of restarting from the brand template.
  savedPayload = null,
  // YouTube's main/second templates when the brand has a second one
  // (hooks/templateSlots.js `templateChoices`); null otherwise, and then the
  // editor looks and works exactly as before.
  templateChoices = null,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [selected, setSelected] = useState(null); // the gallery's pick
  const [template, setTemplate] = useState(null); // the template open in the editor
  const [preselect, setPreselect] = useState({ status: "idle", notice: "" });
  const [dirty, setDirty] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const [confirm, setConfirm] = useState(NO_CONFIRM);
  const docRef = useRef(NO_DOC); // latest document, keyed to the editor instance
  const timeRef = useRef(0); // latest playhead position, for thumbnail capture
  const services = useMemo(() => createEditorServices(), []);
  const [thumbnailCreatorOpen, setThumbnailCreatorOpen] = useState(false);
  const [thumbnailCapturing, setThumbnailCapturing] = useState(false);
  const [thumbnailCapturedFrame, setThumbnailCapturedFrame] = useState(null);

  const userKey = useEditorUserKey();

  // Callers rebuild these objects every render; only their ids should refetch.
  const preselectedRef = useRef(preselectedTemplate);
  preselectedRef.current = preselectedTemplate;
  const horizontalRef = useRef(preselectedHorizontalTemplate);
  horizontalRef.current = preselectedHorizontalTemplate;
  // A saved edit reopens on the template it was made on, even one the user
  // picked from the gallery rather than the brand's assigned template.
  const savedRef = useRef(savedPayload);
  savedRef.current = savedPayload;
  const savedTemplateId = savedTemplateIdOf(savedPayload);
  const preselectedId = preselectedTemplate?.id ?? null;
  const horizontalId = preselectedHorizontalTemplate?.id ?? null;
  const usesGallery = !(preselectedTemplate || preselectedHorizontalTemplate) || preselect.status === "failed";

  // Exactly one raw video picked: edit that clip itself, in place of the
  // watermarked/edited video, without going through the multi-clip merge path.
  const singleRawUrl = rawVideos && rawVideos.length === 1 ? resolveDeliveryUrl(rawVideos[0].url, "s3") : null;
  const videoUrl = singleRawUrl || resolveDeliveryUrl(editSourceUrl(video), "s3");

  // Merging several raw clips instead of the single watermarked/edited video.
  const multiActive = Boolean(rawVideos && rawVideos.length > 1);
  const rawVideoUrls = useMemo(
    () => (multiActive ? rawVideos.map((v) => resolveDeliveryUrl(v.url, "s3")) : null),
    [multiActive, rawVideos],
  );
  const resumeDocument = useMemo(() => resumeDocumentFor(savedPayload, template), [savedPayload, template]);
  const [multiProbe, setMultiProbe] = useState({ status: "idle", sources: [] });

  useEffect(() => {
    if (!open || !rawVideoUrls) {
      setMultiProbe({ status: "idle", sources: [] });
      return undefined;
    }
    let cancelled = false;
    setMultiProbe({ status: "loading", sources: [] });
    Promise.all(rawVideoUrls.map(probeVideoMetadata)).then((results) => {
      if (cancelled) return;
      const sources = rawVideoUrls.map((source, i) => ({
        source,
        duration: results[i].duration,
        width: results[i].width,
        height: results[i].height,
      }));
      setMultiProbe({ status: results.every((r) => r.ok) ? "ready" : "error", sources });
    });
    return () => {
      cancelled = true;
    };
  }, [open, rawVideoUrls]);

  // Which of the brand's templates fits this video's frame. Read only when there
  // is a horizontal one to choose.
  const shape = useVideoMetadata(
    resolveDeliveryUrl(editSourceUrl(video), "s3"),
    open && Boolean(horizontalId),
  );
  const shapeKnown = !horizontalId || shape.status === "ready" || shape.status === "error";
  const landscape = shape.status === "ready" && shape.width > shape.height;

  // ── Lifecycle: reset on close; load the brand's template on open ──────────
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setSelected(null);
      setTemplate(null);
      setPreselect({ status: "idle", notice: "" });
      setDirty(false);
      setEditorReady(false);
      setConfirm(NO_CONFIRM);
      docRef.current = NO_DOC;
      return undefined;
    }
    // Nothing can be chosen until the frame size is read; show the loader.
    if (!shapeKnown) {
      setActiveStep(1);
      setPreselect({ status: "loading", notice: "" });
      return undefined;
    }
    const pre = pickStartTemplate({
      savedPayload: savedRef.current,
      landscape,
      vertical: preselectedRef.current,
      horizontal: horizontalRef.current,
    });
    if (!pre) return undefined;

    let cancelled = false;
    setActiveStep(1);
    setPreselect({ status: "loading", notice: "" });
    loadStartTemplate(pre).then(({ template: chosen, notice }) => {
      if (cancelled) return;
      if (chosen) {
        setTemplate(chosen);
        setSelected(chosen);
        setPreselect({ status: "done", notice: "" });
        return;
      }
      setPreselect({ status: "failed", notice });
      setActiveStep(0);
    });
    return () => {
      cancelled = true;
    };
  }, [open, preselectedId, horizontalId, shapeKnown, landscape, savedTemplateId]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const markDirty = useCallback(() => setDirty(true), []);

  const askConfirm = (title, message, confirmLabel, onConfirm) =>
    setConfirm({ open: true, title, message, confirmLabel, onConfirm });

  const discardEdits = () => {
    docRef.current = NO_DOC;
    setDirty(false);
  };

  // Main / Second switch (YouTube with two templates only).
  const slotPair = pairFor(templateChoices, template?.id);
  const [slotBusy, setSlotBusy] = useState(false);
  const pickSlot = (row) => {
    if (!row || String(row.template_id) === String(template?.id ?? "")) return;
    setSlotBusy(true);
    loadStartTemplate(asPreselected(row))
      .then(({ template: loaded, notice }) => {
        if (!loaded) {
          toast.error(notice || "Could not open that template.");
          return;
        }
        const apply = () => {
          discardEdits();
          setTemplate(loaded);
          setActiveStep(1);
        };
        if (dirty) {
          askConfirm("Start over with this template?", "Your edits were made on a different template and will be lost.", "Use this template", apply);
        } else {
          apply();
        }
      })
      .finally(() => setSlotBusy(false));
  };

  const requestClose = () => {
    if (!dirty) {
      onClose();
      return;
    }
    askConfirm("Discard your edits?", "Closing the editor now loses the changes you made to this video.", "Discard and close", onClose);
  };

  const handleNext = () => {
    if (!selected) return;
    const same = template && template.id === selected.id && template.version === selected.version;
    const apply = () => {
      if (!same) {
        discardEdits();
        setTemplate(selected);
      }
      setActiveStep(1);
    };
    if (!same && dirty) {
      askConfirm("Start over with this template?", "Your edits were made on a different template and will be lost.", "Use this template", apply);
    } else {
      apply();
    }
  };

  const handleBack = () => {
    setSelected(template);
    setActiveStep(0);
  };

  const handleSave = () => {
    const doc = docRef.current.doc;
    if (!template || !editorReady || !doc) {
      toast.error("The editor is still loading — try again in a moment.");
      return;
    }
    if (hasLocalMedia(doc)) {
      toast.error("Some media only exists in this browser (a blob: URL). Remove it or upload it through the editor before saving.");
      return;
    }
    onSave(buildRenderPayload(template, doc));
    onClose();
  };

  const handleCaptureThumbnail = async () => {
    const doc = docRef.current.doc;
    if (!editorReady || !doc || thumbnailCapturing) {
      toast.error("The editor is still loading — try again in a moment.");
      return;
    }
    setThumbnailCapturing(true);
    try {
      const response = await api.post(
        "videos/editor-snapshot/",
        { document: doc, time: timeRef.current },
        { timeout: 100000 },
      );
      setThumbnailCapturedFrame(response.data.data_uri);
      setThumbnailCreatorOpen(true);
    } catch (err) {
      toast.error("Failed to capture frame — please try again.");
    } finally {
      setThumbnailCapturing(false);
    }
  };

  const runConfirm = () => {
    const action = confirm.onConfirm;
    setConfirm((c) => ({ ...c, open: false }));
    if (action) action();
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  let body = null;
  if (open && preselect.status === "loading") {
    body = (
      <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1.5}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Loading the brand template…
        </Typography>
      </Box>
    );
  } else if (open && activeStep === 0) {
    body = <EditorTemplateGallery selectedId={selected?.id ?? null} onSelect={setSelected} notice={preselect.notice} />;
  } else if (open && template && multiActive && multiProbe.status !== "ready") {
    body = (
      <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1.5}>
        {multiProbe.status === "error" ? (
          <Typography variant="body2" color="error">
            Could not read one of the selected raw videos — go back and unselect it, or try again.
          </Typography>
        ) : (
          <>
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Reading the selected videos…
            </Typography>
          </>
        )}
      </Box>
    );
  } else if (open && template) {
    body = (
      <VideoEditorWorkspace
        template={template}
        videoUrl={videoUrl}
        videoSources={multiActive ? multiProbe.sources : undefined}
        video={video}
        services={services}
        userKey={userKey}
        docRef={docRef}
        onDirty={markDirty}
        onReadyChange={setEditorReady}
        active={!confirm.open}
        coverTimestamp={coverTimestamp}
        onCoverTimestampSave={onCoverTimestampSave}
        resumeDocument={resumeDocument}
        timeRef={onThumbnailSave ? timeRef : undefined}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      fullScreen
      disableEnforceFocus
      disableEscapeKeyDown
      PaperProps={{ sx: { height: "100vh", bgcolor: "#f8fafc", borderRadius: 0, overflow: "hidden" } }}
    >
      {/* ── Header: one slim row, so the editor gets the height ─────────── */}
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            px: 2,
            py: 0.5,
            minHeight: 44,
            display: "flex",
            alignItems: "center",
            gap: 2,
            color: "white",
            background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)",
          }}
        >
          <Box display="flex" alignItems="center" gap={1} minWidth={0} flex={1}>
            <MovieFilterIcon sx={{ fontSize: 18, opacity: 0.9, flexShrink: 0 }} />
            <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ flexShrink: 0 }}>
              Video Editor
            </Typography>
            {video?.unique_key && (
              <Typography variant="caption" noWrap sx={{ opacity: 0.7, fontFamily: "monospace", flexShrink: 0 }}>
                {video.unique_key}
              </Typography>
            )}
            {(video?.title || video?.video_title) && (
              <Typography variant="caption" noWrap sx={{ opacity: 0.7, minWidth: 0 }}>
                · {video.title || video.video_title}
              </Typography>
            )}
          </Box>

          {usesGallery && (
            <Stepper activeStep={activeStep} sx={{ ...stepperSx, flexShrink: 0, minWidth: 300 }}>
              {DIALOG_STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
            {slotPair && <TemplateSlotSwitch pair={slotPair} currentId={template?.id} onPick={pickSlot} busy={slotBusy} tone="dark" />}
            <Tooltip title="Close">
              <IconButton
                onClick={requestClose}
                size="small"
                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "white", bgcolor: "rgba(255,255,255,0.15)" } }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: "rgba(67,56,202,0.2)" }} />

      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f8fafc",
          p: activeStep === 1 ? { xs: 1, md: 1.5 } : { xs: 2, md: 3 },
        }}
      >
        {body}
      </DialogContent>

      <Divider sx={{ borderColor: "#e2e8f0" }} />

      <DialogActions sx={{ px: 3, py: 1.5, bgcolor: "#f8fafc", gap: 1 }}>
        {activeStep === 0 ? (
          <>
            <Button onClick={requestClose} variant="outlined" sx={cancelSx}>
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              disabled={!selected}
              onClick={handleNext}
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 700,
                px: 3,
                background: selected ? "linear-gradient(135deg, #312e81, #4338ca)" : undefined,
              }}
            >
              Next: Edit →
            </Button>
          </>
        ) : (
          <>
            {usesGallery ? (
              <Button onClick={handleBack} variant="outlined" sx={indigoOutlineSx}>
                ← Back
              </Button>
            ) : (
              <Button onClick={requestClose} variant="outlined" sx={cancelSx}>
                Cancel
              </Button>
            )}
            {template && (
              <Chip
                size="small"
                icon={<MovieFilterIcon sx={{ fontSize: "14px !important" }} />}
                label={`${template.name}${template.version ? ` · v${template.version}` : ""}${template.fallback ? " · stored copy" : ""}`}
                sx={{ fontWeight: 600, color: "#4338ca", bgcolor: alpha("#4338ca", 0.06), "& .MuiChip-icon": { color: "#4338ca" } }}
              />
            )}
            <Box sx={{ flex: 1 }} />
            {onThumbnailSave && (
              <Button
                variant="outlined"
                startIcon={thumbnailCapturing ? <CircularProgress size={16} color="inherit" /> : <PhotoCameraIcon />}
                onClick={handleCaptureThumbnail}
                disabled={!editorReady || thumbnailCapturing}
                sx={indigoOutlineSx}
              >
                {thumbnailCapturing ? "Capturing…" : "Capture Thumbnail"}
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<MovieFilterIcon />}
              onClick={handleSave}
              disabled={!editorReady}
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: 700,
                px: 3,
                background: "linear-gradient(135deg, #065f46, #10b981)",
                "&:hover": { background: "linear-gradient(135deg, #047857, #059669)" },
              }}
            >
              Save Edit
            </Button>
          </>
        )}
      </DialogActions>

      <Dialog open={confirm.open} onClose={() => setConfirm((c) => ({ ...c, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{confirm.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirm.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirm((c) => ({ ...c, open: false }))} sx={{ textTransform: "none", fontWeight: 600 }}>
            Keep editing
          </Button>
          <Button onClick={runConfirm} color="error" variant="contained" sx={{ textTransform: "none", fontWeight: 700 }}>
            {confirm.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Thumbnail Studio — opened from the "Capture Thumbnail" action above */}
      {onThumbnailSave && (
        <MRSSThumbnailCreator
          open={thumbnailCreatorOpen}
          setOpen={setThumbnailCreatorOpen}
          url={videoUrl}
          capturedFrame={thumbnailCapturedFrame}
          onSave={onThumbnailSave}
        />
      )}
    </Dialog>
  );
}

export default VreVideoEditDialog;
